/**
 * 长转短：任务状态/子块更新等后端接口封装。
 * 业务侧可直接 `import FJLongToShortUpdateStore` 调用；与会话、轮询、解析等仍在 `FJLongToShortStore`。
 */
import FJNetworkManager from '../core/FJNetworkManager';
import FJMessageStore, { MESSAGE_TYPE } from './FJMessageStore';
import FJLocalStore from './FJLocalStore';

const FJLongToShortUpdateStore = {
    async uploadInitialData(block) {
        const { details, eventId } = block;
        const subParams = await Promise.all(
            details.map(item => ({
                id: item.id,
                thumbnail: item.videoData.thumbnailUrl,
                face_data: item?.faceData,
                file_key: item?.videoData?.fileKey,
                job_state: 'succeeded',
            })),
        );

        await FJNetworkManager.updateTaskStatus({ eventId, subParams, status: 'succeeded' });
        return subParams;
    },
    async uploadSubInitialData(eventId, detail) {
        const subParams = [
            {
                id: detail.id,
                thumbnail: detail.videoData.thumbnailUrl,
                face_data: detail?.faceData,
                job_state: 'succeeded',
                file_key: detail?.videoData?.fileKey,
            },
        ];
        await FJNetworkManager.updateTaskStatus({ eventId, subParams });
        return subParams;
    },
    async uploadStatus(eventId, ids, status = 'succeeded') {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                status,
                subParams: ids.map(id => ({ id, job_state: status })),
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async uploadSubStatus(eventId, id, status = 'succeeded') {
        const subParams = [{ id, job_state: status }];
        await FJNetworkManager.updateTaskStatus({ eventId, status, subParams });
        return subParams;
    },

    async renameBlock(eventId, name) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({ eventId, name });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            FJMessageStore.addMessage(FJLocalStore._('updated-successfully'), MESSAGE_TYPE.SUCCESS);
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async deleteBlock(ids) {
        try {
            const res = await FJNetworkManager.deleteSmartCutTasks(ids);
            if (res.code !== 200) {
                this._notifyNetworkError();
            }
            return res;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return { code: 0, data: null };
        }
    },
    async deleteSubBlock(eventId, subIds) {
        try {
            const ids = Array.isArray(subIds) ? subIds : [subIds];
            const res = await FJNetworkManager.deleteSmartCutSubTasks(eventId, ids);
            if (res.code !== 200) {
                this._notifyNetworkError();
            }
            return res;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return { code: 0, data: null };
        }
    },
    async renameSubBlock(eventId, subId, name) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, title: name }],
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            FJMessageStore.addMessage(FJLocalStore._('updated-successfully'), MESSAGE_TYPE.SUCCESS);
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },

    async uploadSubFaceData(eventId, subId, faceData) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, face_data: faceData }],
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async uploadSubThumbnail(eventId, subId, thumbnail) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, thumbnail: thumbnail }],
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async uploadFileKey(eventId, subId, fileKey) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, file_key: fileKey }],
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async updateLike(eventId, subId, liked) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, liked: liked ? 1 : 0 }],
            });
            if (result === undefined) {
                this._notifyNetworkError();
            }
            liked &&
                FJMessageStore.addMessage(
                    FJLocalStore._('thanks-for-your-feedback-weve-recorded-it'),
                    MESSAGE_TYPE.SUCCESS,
                );
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async updateDislike(eventId, subId, disliked, feedback) {
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, disliked: disliked ? 1 : 0 }],
            });
            if (feedback && disliked) {
                await FJNetworkManager.feedback({ eventId, subId, feedback });
            }
            if (result === undefined) {
                this._notifyNetworkError();
            }
            disliked &&
                FJMessageStore.addMessage(
                    FJLocalStore._('thanks-for-your-feedback-the-clip-has-been-hidden'),
                    MESSAGE_TYPE.SUCCESS,
                );
            return result;
        } catch (error) {
            console.error(error);
            this._notifyNetworkError();
            return undefined;
        }
    },
    async publishClip(eventId, subId, publishedList, errorMessageCallback) {
        // 数据是直接覆盖，所以需要传入完整的publishedList（包含已发布的）
        try {
            const result = await FJNetworkManager.updateTaskStatus({
                eventId,
                subParams: [{ id: subId, published_list: publishedList }],
            });
            if (result === undefined) {
                errorMessageCallback ? errorMessageCallback() : this._notifyNetworkError();
            }
            return result;
        } catch (error) {
            console.error(error);
            errorMessageCallback ? errorMessageCallback() : this._notifyNetworkError();
            return undefined;
        }
    },
    _notifyNetworkError() {
        FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), MESSAGE_TYPE.ERROR);
    },
};

const _putFile = () => {};

export default FJLongToShortUpdateStore;
